import React, { useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { Modal, FormField, StatusPicker, DatePicker, TimePicker, NumberField, ChoiceKeys, Combobox, Button, tableWrapCls, thCls, thNumCls, trCls, tdCls, tdNumCls } from '../ui';
import ConfirmDialog from '../ui/ConfirmDialog';
import { JOB_TYPES, DEFAULT_OPERATIONS_STATUSES } from '../../lib/constants';
import { getTruckConflicts, formatTime12 } from '../../utils/formatters';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../auth/AuthContext';
import ActivityLog from '../ActivityLog';

const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

export default function JobModal({ job, onClose, onSave, clients, crew, trucks, defaultDate, initialOverrides, jobs: allJobs, autoOpenRecap = false }) {
    const defaults = { client_id: '', type: 'LOCAL', move_date: defaultDate || new Date().toISOString().split('T')[0], time: '08:00', from_address: '', to_address: '', crew_ids: [], leader_id: null, truck_ids: [], crew_size: 3, duration: 4, duration_days: 1, status: 'unassigned', special_notes: '', heavy_items: '', stairs: false, elevator: false, levels: 1, sqft: 0, packing: '', job_label: '', location_name: '', dispatcher_notes: '', day_sequence: 1, packing_service: '' };
    const [form, setForm] = useState(job ? { ...defaults, ...job } : { ...defaults, ...(initialOverrides || {}) });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const { show: showToast } = useToast();
    const { can } = useAuth();
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // ── Office Recap Entry (sql/073, Jaylen 2026-07-29) ─────────────────
    // When a lead never submits a recap, the office needs a manual path:
    // this writes the SAME move_jobs fields the crew portal writes
    // (actual_* / payment / tip), stamps recap_source='office' so it's
    // never confused with a lead's submission, and completes the job —
    // which fires the existing final-phase → estimate auto-transition.
    // Paid clock hours (crew_day_hours) stay crew/Payroll-owned.
    // autoOpenRecap: the Needs Recap checklist on Active Moves deep-links
    // straight into this panel (open + scrolled into view) — no hunting.
    const [officeRecapOpen, setOfficeRecapOpen] = useState(!!autoOpenRecap);
    const officeRecapRef = React.useRef(null);
    React.useEffect(() => {
        if (autoOpenRecap && officeRecapRef.current) {
            // Let the modal's open animation land first.
            const t = setTimeout(() => officeRecapRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250);
            return () => clearTimeout(t);
        }
    }, [autoOpenRecap]);
    const [or, setOr] = useState({ start: '08:00', end: '16:00', lunch: 30, hoursOverride: '', payment_type: '', payment_amount: '', tip_type: '', tip_total: '', notes: '', damage: '' });
    const setOrF = (k, v) => setOr(o => ({ ...o, [k]: v }));

    const computedOfficeHours = (() => {
        const toMin = (t) => { const [h, m] = String(t || '').split(':').map(Number); return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0); };
        let span = toMin(or.end) - toMin(or.start);
        if (span <= 0) span += 24 * 60; // overnight
        span -= Number(or.lunch) || 0;
        return Math.max(0, Math.round((span / 60) * 4) / 4);
    })();

    const officeHours = or.hoursOverride !== '' ? Number(or.hoursOverride) : computedOfficeHours;

    const handleOfficeRecapSave = async () => {
        if (!(officeHours > 0)) { showToast('Billed hours must be greater than zero', 'error'); return; }
        if (Number(or.payment_amount) < 0 || Number(or.tip_total) < 0) { showToast('Amounts cannot be negative', 'error'); return; }
        setSaving(true);
        try {
            await onSave({
                ...form,
                actual_start_time: or.start,
                actual_end_time: or.end,
                actual_lunch_minutes: Number(or.lunch) || 0,
                actual_hours: officeHours,
                payment_type: or.payment_type || form.payment_type || null,
                payment_amount: or.payment_amount !== '' ? Number(or.payment_amount) : form.payment_amount,
                tip_type: or.tip_type || form.tip_type || null,
                tip_total: or.tip_total !== '' ? Number(or.tip_total) : form.tip_total,
                crew_recap_notes: or.notes || form.crew_recap_notes || '',
                damage_notes: or.damage || form.damage_notes || '',
                recap_submitted_at: new Date().toISOString(),
                recap_submitted_by: null, // office entry — not a crew submission
                recap_source: 'office',
                status: 'completed',
            });
            onClose();
        } catch (e) {
            showToast('Save failed: ' + (e.message || 'Unknown error'), 'error');
        } finally { setSaving(false); }
    };

    const populateClientDetails = useCallback((clientId) => {
        const c = clients.find(cl => cl.id === clientId);
        if (!c) return;
        setForm(f => ({
            ...f,
            client_id: clientId,
            from_address: c.origin_address || f.from_address || '',
            to_address: c.destination_address || f.to_address || '',
            heavy_items: c.heavy_items || f.heavy_items || '',
            special_notes: c.notes || f.special_notes || '',
            type: c.job_type || f.type || 'LOCAL',
            stairs: c.from_stairs || c.to_stairs || f.stairs || false,
            sqft: c.from_sqft || f.sqft || 0,
            levels: c.from_levels || f.levels || 1,
            packing: c.packing_needed || f.packing || '',
        }));
    }, [clients]);

    const toggleCrew = (id) => {
        setForm(f => {
            const has = (f.crew_ids || []).includes(id);
            const newCrew = has ? f.crew_ids.filter(c => c !== id) : [...(f.crew_ids || []), id];
            const newLeader = (!has && newCrew.length === 1) ? id : (has && f.leader_id === id ? (newCrew[0] || null) : f.leader_id);
            return { ...f, crew_ids: newCrew, leader_id: newLeader, crew_size: newCrew.length || f.crew_size };
        });
    };
    const toggleTruck = (id) => setForm(f => ({ ...f, truck_ids: (f.truck_ids || []).includes(id) ? f.truck_ids.filter(t => t !== id) : [...(f.truck_ids || []), id] }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const status = (form.crew_ids || []).length > 0 && form.status === 'unassigned' ? 'assigned' : form.status;
            await onSave({ ...form, status });
            onClose();
        } catch (e) {
            // Sonner toast, NOT a native alert (QA M2 re-verify NEW-4):
            // the alert was invisible under automation / some setups, so
            // failing saves (e.g. NEW-3's constraint violation) looked
            // like a dead Save button with the job silently unchanged.
            showToast('Save failed: ' + (e.message || 'Unknown error'), 'error');
        }
        finally { setSaving(false); }
    };

    const linkedClient = clients.find(c => c.id === form.client_id);

    return (
        <Modal title={job?.id ? 'Edit Job — Dispatch Fields' : 'New Job'} onClose={onClose} width="max-w-2xl">
            {!job?.id && (
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Client"><Combobox items={clients.map(c => ({ value: c.id, label: c.name, sub: c.phone || undefined }))} value={form.client_id || ''} onChange={v => { if (v) populateClientDetails(v); else set('client_id', ''); }} placeholder="Select client..." /></FormField>
                    <FormField label="Job Type"><Combobox items={JOB_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))} value={form.type} onChange={v => set('type', v)} clearable={false} placeholder="Job type" /></FormField>
                </div>
            )}

            {job?.id && (
                <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Move Details (read-only)</span>
                        <span className="text-[10px] text-slate-400">Edit on the Estimate or Client record</span>
                    </div>
                    <div className="text-xs space-y-1 text-slate-600">
                        <div><strong>Client:</strong> {linkedClient?.name || 'Unknown'} &bull; <strong>Type:</strong> {form.type}</div>
                        <div><strong>From:</strong> {form.from_address || 'Not set'}</div>
                        <div><strong>To:</strong> {form.to_address || 'Not set'}</div>
                        {form.heavy_items && <div><strong>Heavy Items:</strong> {form.heavy_items}</div>}
                        {form.special_notes && <div><strong>Notes:</strong> {form.special_notes}</div>}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-3 gap-4">
                <FormField label="Date"><DatePicker className={inputCls} value={form.move_date} onChange={v => set('move_date', v)} placeholder="Select date..." /></FormField>
                <FormField label="Time"><TimePicker value={form.time || ''} onChange={v => set('time', v)} ariaLabel="Job time" /></FormField>
                <FormField label="Duration (hrs)"><NumberField value={form.duration} onChange={v => set('duration', parseInt(v) || 1)} step={1} min={1} suffix="hrs" /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <FormField label="Operations Status"><StatusPicker statuses={DEFAULT_OPERATIONS_STATUSES} value={form.status} onChange={v => set('status', v)} mode="dropdown" /></FormField>
                <div className="flex items-center gap-2 pt-5">
                    <label className="text-slate-600 text-sm">Day Order:</label>
                    <ChoiceKeys
                        items={[{ value: '1', label: '1st' }, { value: '2', label: '2nd' }, { value: '3', label: '3rd' }, { value: '4', label: '4th' }]}
                        value={form.day_sequence || 1}
                        onChange={v => set('day_sequence', parseInt(v))}
                        size="sm"
                        ariaLabel="Day order"
                    />
                    {(form.duration_days || 1) > 1 && <span className="text-xs text-amber-600">{form.duration_days}d trip</span>}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <FormField label="Job Label"><input className={inputCls} value={form.job_label || ''} onChange={e => set('job_label', e.target.value)} placeholder="Storage Move, Small Move, Load..." /></FormField>
                <FormField label="Location Name"><input className={inputCls} value={form.location_name || ''} onChange={e => set('location_name', e.target.value)} placeholder="Extra Space Storage, etc." /></FormField>
            </div>
            <FormField label="Dispatcher Notes (for crew)"><textarea className={inputCls} rows="2" value={form.dispatcher_notes || ''} onChange={e => set('dispatcher_notes', e.target.value)} placeholder="BOL on locker, client bought blankets, no stairs for piano..." /></FormField>
            <FormField label="Packing Service">
                <div className="flex gap-2">
                    {['', 'FULL', 'PARTIAL'].map(v => (
                        <button key={v} type="button" onClick={() => set('packing_service', v)}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium border ${form.packing_service === v ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-300'}`}>
                            {v || 'None'}
                        </button>
                    ))}
                </div>
            </FormField>
            <FormField label="Assign Crew">
                <div className="flex flex-wrap gap-2">
                    {crew.filter(c => c.active !== false).map(c => {
                        const selected = (form.crew_ids || []).includes(c.id);
                        const isLeader = form.leader_id === c.id;
                        return (
                            <div key={c.id} className="flex items-center gap-1">
                                <button type="button" onClick={() => toggleCrew(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 ${selected ? 'text-white' : 'bg-white text-slate-600 border-slate-300'}`} style={selected ? { backgroundColor: c.display_color, borderColor: c.display_color } : {}}>{c.name}</button>
                                {selected && <button type="button" onClick={() => set('leader_id', c.id)} className={`text-xs px-1.5 py-0.5 rounded ${isLeader ? 'bg-yellow-400 text-yellow-900 font-bold' : 'bg-slate-200 text-slate-500'}`}>L</button>}
                            </div>
                        );
                    })}
                </div>
            </FormField>
            <FormField label="Assign Trucks">
                <div className="flex flex-wrap gap-2">
                    {trucks.map(t => {
                        const selected = (form.truck_ids || []).includes(t.id);
                        // Same-lead rule (2026-08-07): this job's lead re-using
                        // a truck across his own same-day jobs isn't a
                        // conflict. Cross-crew overlap shows as a clickable
                        // amber warn (handoffs are real), not a hard block.
                        const conflict = allJobs ? getTruckConflicts(t.id, form.move_date, form.duration, form.time, form.id, allJobs, form.leader_id) : null;
                        const isConflict = conflict && !selected;
                        return (
                            <div key={t.id} className="relative">
                                <button type="button" onClick={() => toggleTruck(t.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${selected ? 'bg-blue-600 text-white border-blue-600' : isConflict ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-slate-600 border-slate-300'}`} title={isConflict ? `On another crew's job: ${conflict.map(j => formatTime12(j.time) + ' (' + (j.duration||4) + 'h)').join(', ')} — tap to share anyway` : ''}>{t.name}{isConflict ? ' (in use)' : ''}</button>
                            </div>
                        );
                    })}
                </div>
            </FormField>

            {/* Daily Recap — surfaces what the crew lead submitted from
                the crew portal (CrewPortalPage.jsx → RecapForm). Only
                shown if a recap exists. Read-only here; recaps are
                authored by crew leads and the office shouldn't be
                editing them after the fact (the values feed the Hours
                page rollups and would distort the payroll picture). */}
            {job?.id && job?.recap_submitted_at && (
                <div className="mt-6 border border-emerald-200 bg-emerald-50/50 rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-emerald-100/60 border-b border-emerald-200 flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                            Daily Recap
                        </span>
                        <span className="text-[10px] text-emerald-700">
                            Submitted {(() => {
                                // recap_submitted_at is a full timestamptz, not a
                                // HH:MM string — formatTime12 expects the latter,
                                // so use date-fns format() here instead.
                                try { return format(parseISO(job.recap_submitted_at), 'MMM d, h:mm a'); } catch { return ''; }
                            })()}
                            {/* recap_submitted_by holds the crew member's UUID
                                for portal submissions (QA M5 OBS-2) — resolve
                                to a name; legacy rows with a raw name fall
                                through unchanged. Office manual entries
                                (recap_source='office', sql/073) show as such —
                                who exactly is in the audit log. */}
                            {job.recap_source === 'office'
                                ? ' — entered by office'
                                : (job.recap_submitted_by ? ` by ${crew.find(c => c.id === job.recap_submitted_by)?.name || job.recap_submitted_by}` : '')}
                        </span>
                    </div>
                    <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Start</div>
                            <div className="font-semibold text-slate-800">{job.actual_start_time ? formatTime12(job.actual_start_time) : '—'}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">End</div>
                            <div className="font-semibold text-slate-800">{job.actual_end_time ? formatTime12(job.actual_end_time) : '—'}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hours</div>
                            <div className="font-semibold text-slate-800">{job.actual_hours != null ? `${job.actual_hours}h` : '—'}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Lunch</div>
                            <div className="font-semibold text-slate-800">{job.actual_lunch_minutes != null ? `${job.actual_lunch_minutes} min` : '—'}</div>
                        </div>
                        {job.customer_rating > 0 && (
                            <div className="col-span-2 sm:col-span-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer satisfaction</div>
                                <div className="font-semibold text-slate-800">
                                    {'★'.repeat(job.customer_rating)}<span className="text-slate-300">{'★'.repeat(5 - job.customer_rating)}</span>
                                    <span className="ml-2 text-[11px] text-slate-500">{job.customer_rating}/5</span>
                                </div>
                            </div>
                        )}
                        {job.crew_recap_notes && (
                            <div className="col-span-2 sm:col-span-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Crew notes</div>
                                <div className="text-slate-700 whitespace-pre-wrap">{job.crew_recap_notes}</div>
                            </div>
                        )}
                        {job.damage_notes && (
                            <div className="col-span-2 sm:col-span-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Damage notes</div>
                                <div className="text-rose-800 whitespace-pre-wrap bg-rose-50 border border-rose-100 rounded px-2 py-1">{job.damage_notes}</div>
                            </div>
                        )}
                        {/* Materials used (sql/044). Admin-only view —
                            surfaces the recap snapshot with unit-price
                            revenue and (cost separately) gross profit so
                            office can see the line-item contribution at
                            a glance. Skipped when the recap had no
                            materials. */}
                        {(() => {
                            const items = Array.isArray(job.materials_used?.items) ? job.materials_used.items : [];
                            if (items.length === 0) return null;
                            const revenue = items.reduce((s, it) => s + (Number(it.count) || 0) * (Number(it.unit_price) || 0), 0);
                            const cost    = items.reduce((s, it) => s + (Number(it.count) || 0) * (Number(it.unit_cost)  || 0), 0);
                            // Sales tax snapshot (2026-07-09) — present on recaps
                            // submitted after the tax rollout; older recaps just
                            // omit the line.
                            const tax = Number(job.materials_used?.tax);
                            const taxRate = Number(job.materials_used?.tax_rate_pct);
                            return (
                                <div className="col-span-2 sm:col-span-4">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
                                        <span>Materials used</span>
                                        <span className="text-[10px] font-semibold text-slate-600 tabular-nums">
                                            Revenue ${revenue.toFixed(2)}
                                            {Number.isFinite(tax) && tax > 0 && (
                                                <span className="text-sky-700 ml-2">+ Tax{Number.isFinite(taxRate) ? ` (${taxRate}%)` : ''} ${tax.toFixed(2)}</span>
                                            )}
                                            <span className="text-slate-400 ml-2">Cost ${cost.toFixed(2)}</span>
                                            <span className="text-emerald-700 ml-2">GP ${(revenue - cost).toFixed(2)}</span>
                                        </span>
                                    </div>
                                    {/* Materials table — ledger recipe (BBM Context for claude/lists.md §3), smaller text */}
                                    <div className={tableWrapCls}>
                                        <table className="w-full text-[12px]">
                                            <thead>
                                                <tr>
                                                    <th className={thCls}>Item</th>
                                                    <th className={`${thNumCls} w-16`}>Qty</th>
                                                    <th className={`${thNumCls} w-20`}>Unit $</th>
                                                    <th className={`${thNumCls} w-20`}>Line $</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((it, i) => {
                                                    const line = (Number(it.count) || 0) * (Number(it.unit_price) || 0);
                                                    return (
                                                        <tr key={`${it.id}-${i}`} className={trCls}>
                                                            <td className={`${tdCls} text-slate-800 font-medium`}>
                                                                {it.name}
                                                                <span className="text-slate-400 ml-1 text-[10px]">({it.unit})</span>
                                                            </td>
                                                            <td className={tdNumCls}>{Number(it.count) || 0}</td>
                                                            <td className={`${tdNumCls} text-slate-600`}>${(Number(it.unit_price) || 0).toFixed(2)}</td>
                                                            <td className={`${tdNumCls} font-bold text-slate-900`}>${line.toFixed(2)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}
                        {/* Payment + BOL + reset-truck/check photos — all
                            captured by the crew lead at recap submission and
                            stored as flat columns on move_jobs (sql/043).
                            Each row is independent; if a photo is missing
                            the column collapses cleanly. */}
                        {/* Packing billing mode (sql/045). Surfaces only
                            on PACKING jobs that recorded one. Lives
                            adjacent to Payment so the relationship is
                            visible. */}
                        {job.type === 'PACKING' && job.packing_billing_mode && (
                            <div className="col-span-2 sm:col-span-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Packing billing</div>
                                <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                    job.packing_billing_mode === 'collected_today'
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-amber-100 text-amber-800'
                                }`}>
                                    {job.packing_billing_mode === 'collected_today'
                                        ? 'Collected today'
                                        : "Bill with move's recap"}
                                </span>
                            </div>
                        )}
                        {(job.payment_type || (job.payment_amount > 0)) && (
                            <div className="col-span-2 sm:col-span-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment collected</div>
                                <div className="inline-flex items-center gap-2 text-sm">
                                    <span className="font-bold text-slate-900 tabular-nums">${Number(job.payment_amount || 0).toFixed(2)}</span>
                                    {job.payment_type && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                            job.payment_type === 'cash'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : job.payment_type === 'card'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-violet-100 text-violet-700'
                                        }`}>
                                            {job.payment_type}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {job.bol_photo_url && (
                            <div className="col-span-2 sm:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                    {job.type === 'LONG_DISTANCE' ? 'Long-distance BOL' : 'Local BOL'}
                                </div>
                                <a href={job.bol_photo_url} target="_blank" rel="noopener noreferrer"
                                    className="inline-block rounded-lg overflow-hidden border border-amber-200 hover:border-amber-400 transition-colors">
                                    <img src={job.bol_photo_url} alt="BOL" className="max-h-40 max-w-full object-contain bg-slate-100" loading="lazy" />
                                </a>
                            </div>
                        )}
                        {job.payment_check_photo_url && (
                            <div className="col-span-2 sm:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Check photo</div>
                                <a href={job.payment_check_photo_url} target="_blank" rel="noopener noreferrer"
                                    className="inline-block rounded-lg overflow-hidden border border-violet-200 hover:border-violet-400 transition-colors">
                                    <img src={job.payment_check_photo_url} alt="Check" className="max-h-40 max-w-full object-contain bg-slate-100" loading="lazy" />
                                </a>
                            </div>
                        )}
                        {job.recap_photo_url && (
                            <div className="col-span-2 sm:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Reset truck photo</div>
                                <a
                                    href={job.recap_photo_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block rounded-lg overflow-hidden border border-slate-200 hover:border-red-300 transition-colors"
                                >
                                    <img
                                        src={job.recap_photo_url}
                                        alt="Reset truck"
                                        className="max-h-40 max-w-full object-contain bg-slate-100"
                                        loading="lazy"
                                    />
                                </a>
                            </div>
                        )}
                        {/* Stage 5: fuel receipts (sql/046). Array of
                            { photo_url, gallons, amount }. Surfaces
                            only for LONG_DISTANCE jobs (no fuel
                            captured on other types). Shows running
                            totals across all receipts for the leg. */}
                        {(() => {
                            const receipts = Array.isArray(job.fuel_receipts) ? job.fuel_receipts : [];
                            if (receipts.length === 0) return null;
                            const totalGallons = receipts.reduce((s, r) => s + (Number(r.gallons) || 0), 0);
                            const totalAmount  = receipts.reduce((s, r) => s + (Number(r.amount)  || 0), 0);
                            return (
                                <div className="col-span-2 sm:col-span-4">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
                                        <span>Fuel receipts ({receipts.length})</span>
                                        <span className="tabular-nums text-slate-600">
                                            {totalGallons > 0 && <>{totalGallons.toFixed(2)} gal</>}
                                            {totalGallons > 0 && totalAmount > 0 && <span className="text-slate-400 mx-1">·</span>}
                                            {totalAmount > 0 && <>${totalAmount.toFixed(2)}</>}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {receipts.map((r, i) => (
                                            <div key={r.id || i} className="border border-orange-200 rounded-lg overflow-hidden bg-white">
                                                {r.photo_url ? (
                                                    <a href={r.photo_url} target="_blank" rel="noopener noreferrer" title="Open full-size">
                                                        <img src={r.photo_url} alt="Fuel receipt" className="w-24 h-24 object-cover" loading="lazy" />
                                                    </a>
                                                ) : (
                                                    <div className="w-24 h-24 flex items-center justify-center text-[10px] text-slate-400 bg-slate-50">No photo</div>
                                                )}
                                                <div className="px-1.5 py-1 text-[10px] tabular-nums text-slate-700 text-center">
                                                    {r.gallons != null && <>{Number(r.gallons).toFixed(2)} gal</>}
                                                    {r.gallons != null && r.amount != null && <span className="text-slate-400"> · </span>}
                                                    {r.amount != null && <>${Number(r.amount).toFixed(2)}</>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                        {(job.odometer_photo_url || job.odometer_miles != null) && (
                            <div className="col-span-2 sm:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
                                    <span>Odometer</span>
                                    {job.odometer_miles != null && (
                                        <span className="font-bold text-slate-700 tabular-nums">{Number(job.odometer_miles).toLocaleString()} mi</span>
                                    )}
                                </div>
                                {job.odometer_photo_url ? (
                                    <a href={job.odometer_photo_url} target="_blank" rel="noopener noreferrer"
                                        className="inline-block rounded-lg overflow-hidden border border-cyan-200 hover:border-cyan-400 transition-colors">
                                        <img src={job.odometer_photo_url} alt="Odometer" className="max-h-40 max-w-full object-contain bg-slate-100" loading="lazy" />
                                    </a>
                                ) : (
                                    <span className="text-slate-300 text-xs">Photo not attached</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Office Recap Entry (sql/073) ──────────────────────────
                Only when NO recap exists and the user can manage dispatch.
                Writes the same fields the crew portal writes, marks the
                source as office, and completes the job. */}
            {job?.id && !job?.recap_submitted_at && job?.type !== 'WALKTHROUGH' && can('manage_dispatch') && (
                <div ref={officeRecapRef} className="mt-6 border border-amber-200 bg-amber-50/40 rounded-xl overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setOfficeRecapOpen(v => !v)}
                        className="w-full px-4 py-2.5 bg-amber-100/60 border-b border-amber-200 flex items-center justify-between text-left"
                    >
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
                            No recap submitted — enter one manually
                        </span>
                        <span className="text-[11px] font-semibold text-amber-700">{officeRecapOpen ? 'Close' : 'Enter Recap (office)'}</span>
                    </button>
                    {officeRecapOpen && (
                        <div className="px-4 py-4 space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Start</div>
                                    <TimePicker value={or.start} onChange={v => setOrF('start', v)} ariaLabel="Recap start time" compact />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">End</div>
                                    <TimePicker value={or.end} onChange={v => setOrF('end', v)} ariaLabel="Recap end time" compact />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Lunch (min)</div>
                                    <NumberField value={or.lunch} onChange={v => setOrF('lunch', Math.max(0, Number(v) || 0))} step={15} min={0} suffix="min" ariaLabel="Lunch minutes" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Billed hours</div>
                                    <NumberField
                                        placeholder={`${computedOfficeHours}`}
                                        value={or.hoursOverride}
                                        onChange={v => setOrF('hoursOverride', v === '' ? '' : Math.max(0, Number(v) || 0))}
                                        step={0.25} min={0} suffix="hrs" ariaLabel="Billed hours" />
                                    <div className="text-[10px] text-slate-400 mt-0.5">auto {computedOfficeHours}h — type to override</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment collected</div>
                                    <div className="flex items-center gap-1.5">
                                        {['cash', 'card', 'check'].map(t => (
                                            <button key={t} type="button"
                                                onClick={() => setOrF('payment_type', or.payment_type === t ? '' : t)}
                                                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase border transition-colors ${or.payment_type === t ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-400'}`}>
                                                {t}
                                            </button>
                                        ))}
                                        <NumberField className="flex-1" placeholder="0.00" prefix="$" step={5} min={0}
                                            value={or.payment_amount}
                                            onChange={v => setOrF('payment_amount', v)} ariaLabel="Payment amount" />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tip</div>
                                    <div className="flex items-center gap-1.5">
                                        {['cash', 'card', 'check'].map(t => (
                                            <button key={t} type="button"
                                                onClick={() => setOrF('tip_type', or.tip_type === t ? '' : t)}
                                                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase border transition-colors ${or.tip_type === t ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400'}`}>
                                                {t}
                                            </button>
                                        ))}
                                        <NumberField className="flex-1" placeholder="0.00" prefix="$" step={5} min={0}
                                            value={or.tip_total}
                                            onChange={v => setOrF('tip_total', v)} ariaLabel="Tip amount" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
                                    <textarea className={`${inputCls} min-h-[60px]`} placeholder="How the job went, why the office is entering this..."
                                        value={or.notes} onChange={e => setOrF('notes', e.target.value)} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-rose-500 mb-1">Damage (if any)</div>
                                    <textarea className={`${inputCls} min-h-[60px]`} placeholder="Leave blank if none"
                                        value={or.damage} onChange={e => setOrF('damage', e.target.value)} />
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-3 pt-1">
                                <p className="text-[11px] text-slate-500">
                                    Saves billed hours ({officeHours}h), marks the job <span className="font-semibold">Completed</span>, and tags the recap as office-entered. Paid clock hours are managed on the Payroll page.
                                </p>
                                <Button onClick={handleOfficeRecapSave} loading={saving}>
                                    Save Recap & Complete
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Activity feed — audit log of every dispatch-field change on
                this job (crew, trucks, time, status, etc.). Sourced from
                audit_log via the Postgres trigger in sql/030_audit_log.sql.
                Only shown when editing an existing job. */}
            {job?.id && (
                <div className="mt-6">
                    <ActivityLog tableName="move_jobs" recordId={job.id} />
                </div>
            )}

            <div className="flex justify-between mt-6">
                <div>{job?.id && <Button variant="danger" onClick={() => setDeleteConfirm(true)}>Delete Job</Button>}</div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} loading={saving}>{job?.id ? 'Save Changes' : 'Create Job'}</Button>
                </div>
            </div>

            {/* In-app delete confirm (feedback.md 2B: destructive, names the record). */}
            <ConfirmDialog
                open={deleteConfirm}
                title={`Delete ${linkedClient?.name ? `${linkedClient.name}'s` : 'this'} job${form.move_date ? ` on ${form.move_date}` : ''}?`}
                confirmLabel="Delete job"
                danger
                onConfirm={async () => {
                    setDeleteConfirm(false);
                    try {
                        await onSave(null, job.id);
                        onClose();
                    } catch (e) {
                        showToast('Delete failed: ' + (e.message || 'Unknown error'), 'error');
                    }
                }}
                onCancel={() => setDeleteConfirm(false)}>
                The job comes off the dispatch board and any crew or truck assigned to it are freed up. The estimate and client record are kept. This can't be undone.
            </ConfirmDialog>
        </Modal>
    );
}
